import React, { useMemo } from 'react';
import SpecBottomSheet from '../SpecBottomSheet/SpecBottomSheet';
import './AddToCalendarSheet.css';

export interface CalendarEvent {
  title: string;
  description?: string;
  location?: string;
  /** ISO date string or parseable date for the start */
  startDate: string;
  /** ISO date string or parseable date for the end (defaults to startDate + 2h) */
  endDate?: string;
}

export interface AddToCalendarSheetProps {
  open: boolean;
  onClose: () => void;
  event: CalendarEvent;
}

// Device detection
const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
const isAndroid = () => /Android/.test(navigator.userAgent);
const isMobile = () => isIOS() || isAndroid();

function toUTCString(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
}

function defaultEndDate(startStr: string): string {
  const d = new Date(startStr);
  d.setHours(d.getHours() + 2);
  return d.toISOString();
}

function buildGoogleUrl(event: CalendarEvent): string {
  const start = toUTCString(event.startDate);
  const end = toUTCString(event.endDate || defaultEndDate(event.startDate));
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.title,
    dates: `${start}/${end}`,
    details: event.description || '',
    location: event.location || '',
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

function buildIcsDataUrl(event: CalendarEvent): string {
  const start = toUTCString(event.startDate);
  const end = toUTCString(event.endDate || defaultEndDate(event.startDate));
  const uid = `${Date.now()}@spec.app`;

  const content = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Spec//Spec App//EN',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTART:${start}`,
    `DTEND:${end}`,
    `SUMMARY:${event.title}`,
    `DESCRIPTION:${(event.description || '').replace(/\n/g, '\\n')}`,
    `LOCATION:${event.location || ''}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');

  return 'data:text/calendar;charset=utf-8,' + encodeURIComponent(content);
}

const GoogleIcon: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 48 48">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59a14.5 14.5 0 0 1 0-9.18l-7.98-6.19a24.0 24.0 0 0 0 0 21.56l7.98-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

const AppleIcon: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
    <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </svg>
);

const CalendarIcon: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
    <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11zM9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm-8 4H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2z"/>
  </svg>
);

const AddToCalendarSheet: React.FC<AddToCalendarSheetProps> = ({ open, onClose, event }) => {
  const deviceInfo = useMemo(() => ({
    isIOS: isIOS(),
    isAndroid: isAndroid(),
    isMobile: isMobile(),
  }), []);

  // Pre-build URLs so they're ready when user taps
  const googleUrl = useMemo(() => buildGoogleUrl(event), [event]);
  const icsDataUrl = useMemo(() => buildIcsDataUrl(event), [event]);
  const filename = useMemo(() => `${event.title.replace(/[^a-zA-Z0-9]/g, '_')}.ics`, [event.title]);

  const handleGoogleClick = () => {
    // Open URL immediately (must be synchronous for mobile browsers)
    window.open(googleUrl, '_blank');
    // Then close sheet
    onClose();
  };

  const handleIcsClick = () => {
    // Create and click link immediately (must be synchronous for mobile browsers)
    const link = document.createElement('a');
    link.href = icsDataUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Then close sheet
    onClose();
  };

  return (
    <SpecBottomSheet open={open} onClose={onClose} title="Add to Calendar">
      <div className="add-to-calendar__content">
        {/* Google Calendar */}
        <button
          type="button"
          className="add-to-calendar__link-button add-to-calendar__link-button--dark"
          onClick={handleGoogleClick}
        >
          <GoogleIcon />
          <span>Google Calendar</span>
        </button>

        {/* Apple/ICS Calendar */}
        <button
          type="button"
          className="add-to-calendar__link-button add-to-calendar__link-button--dark"
          onClick={handleIcsClick}
        >
          <AppleIcon />
          <span>{deviceInfo.isIOS ? 'Apple Calendar' : 'Apple / iCal'}</span>
        </button>

        {/* Download option */}
        <button
          type="button"
          className="add-to-calendar__link-button add-to-calendar__link-button--accent"
          onClick={handleIcsClick}
        >
          <CalendarIcon />
          <span>{deviceInfo.isMobile ? 'Other Calendar Apps' : 'Download .ics File'}</span>
        </button>
      </div>
    </SpecBottomSheet>
  );
};

export default AddToCalendarSheet;
